import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Flame, Mail } from "lucide-react";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { isValidEmail, sanitize } from "@/lib/validation";
import { getAuthErrorMessage } from "@/lib/auth-errors";
import { getAuthRedirectBase } from "@/lib/auth-redirect";
import { useAuditLog } from "@/hooks/useAuditLog";
import { waitForSession, ensureProfile } from "@/lib/oauth-helpers";
import {
  savePendingInviteToken,
  redeemInviteToken,
  clearPendingInviteToken,
  describeInviteError,
} from "@/lib/inviteToken";

function getStrength(pw: string) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { label: "Weak", color: "bg-destructive", pct: 25 };
  if (score === 2) return { label: "Fair", color: "bg-orange-500", pct: 50 };
  if (score === 3) return { label: "Good", color: "bg-yellow-500", pct: 75 };
  return { label: "Strong", color: "bg-green-500", pct: 100 };
}

export default function Signup() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const { logAction } = useAuditLog();
  const strength = password ? getStrength(password) : null;

  // Persist the invite token so it can be redeemed after email confirmation +
  // first sign-in, even if the user closes the tab.
  useEffect(() => {
    if (inviteToken) savePendingInviteToken(inviteToken);
  }, [inviteToken]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = "Email is required";
    else if (!isValidEmail(email.trim())) errs.email = "Enter a valid email address";

    if (!password) {
      errs.password = "Password is required";
    } else {
      const hasMinLength = password.length >= 8;
      const hasUppercase = /[A-Z]/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSpecialChar = /[^A-Za-z0-9]/.test(password);
      if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
        errs.password = "Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.";
      } else if (email.trim() && password.toLowerCase() === email.trim().toLowerCase()) {
        errs.password = "Password cannot be the same as your email address.";
      }
    }

    if (!confirmPassword) errs.confirmPassword = "Confirm password is required";
    else if (password !== confirmPassword) errs.confirmPassword = "Passwords do not match";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    // Preserve the invite token across the email-confirmation redirect.
    const authRedirectBase = getAuthRedirectBase();
    const redirectTo = inviteToken
      ? `${authRedirectBase}/login?invite=${encodeURIComponent(inviteToken)}`
      : authRedirectBase;
    const { data, error } = await supabase.auth.signUp({
      email: sanitize(email),
      password,
      options: { emailRedirectTo: redirectTo },
    });
    setLoading(false);

    // With leaked-password/anti-enumeration protection on, Supabase does NOT
    // return an error for an already-registered email — it returns 200 with
    // no session and an empty identities array (a fake "success" so the
    // response can't be used to probe which emails have accounts). Any
    // error-based "already registered" check silently never fires against
    // this project.
    const isAlreadyRegistered =
      (!error && !data.session && (data.user as any)?.identities?.length === 0) ||
      error?.message?.toLowerCase().includes("already registered") ||
      error?.message?.toLowerCase().includes("already been registered") ||
      (error as any)?.code === "user_already_exists";

    if (isAlreadyRegistered) {
      // This email already has an account — signUp() was a no-op, not a real
      // signup. If they came from an invite link, sending them to "check
      // your email" would silently drop the invite (they'd never be
      // prompted to sign in, so accept_invite() never runs). Route them to
      // login instead, with the invite token preserved.
      if (inviteToken) {
        toast.info("You already have an account with this email. Sign in to accept the invitation.");
        navigate(`/login?invite=${encodeURIComponent(inviteToken)}`);
        return;
      }
      logAction("signup", "auth");
      toast.success("Account created! Check your email to confirm.");
      navigate("/check-email", { state: { email: sanitize(email) } });
      return;
    }

    if (error) {
      toast.error(getAuthErrorMessage(error));
      return;
    }
    logAction("signup", "auth");

    // If email confirmation is disabled the user is already signed in — redeem now.
    if (data.session && inviteToken) {
      const result = await redeemInviteToken(inviteToken);
      if (result.ok) {
        clearPendingInviteToken();
        toast.success("Welcome to the team!");
        navigate(result.role === "staff" ? "/tasks" : "/");
        return;
      }
      const message = result.error ? describeInviteError(result.error) : null;
      toast.warning(message ? `Account created, but ${message[0].toLowerCase()}${message.slice(1)}` : "Account created — try signing in to join your team.");
      navigate("/login");
      return;
    }

    toast.success("Account created! Check your email to confirm.");
    navigate("/check-email", { state: { email: sanitize(email) } });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
            <Flame className="h-6 w-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">
            {inviteToken ? "Accept your team invitation" : "Create your account"}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {inviteToken
              ? "Set a password to join your team on LegacyGrowth Engine"
              : "Start managing your home care leads"}
          </p>
        </CardHeader>
        <CardContent>
          {inviteToken && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
              <Mail className="mt-0.5 h-4 w-4 text-primary" />
              <div className="text-xs text-foreground">
                <p className="font-medium">You're joining a team.</p>
                <p className="text-muted-foreground">Use the same email address your invitation was sent to. We'll add you to the agency once you sign in.</p>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required maxLength={255} />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} maxLength={128} />
              {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
              {strength && (
                <div className="space-y-1">
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${strength.color}`} style={{ width: `${strength.pct}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground">Password strength: <span className="font-medium">{strength.label}</span></p>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <PasswordInput id="confirmPassword" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={8} maxLength={128} />
              {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword}</p>}
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Creating account…" : inviteToken ? "Create account & join team" : "Sign Up"}
            </Button>
          </form>
          <div className="my-4 flex items-center gap-2">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground">OR</span>
            <Separator className="flex-1" />
          </div>
          <Button
            variant="outline"
            className="w-full"
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              if (inviteToken) savePendingInviteToken(inviteToken);
              const { error } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                  redirectTo: getAuthRedirectBase(),
                },
              });
              if (error) {
                toast.error(error.message || "Google sign-in failed");
                setLoading(false);
                return;
              }
              // Tokens came back inline — wait for the session to land on
              // the Supabase client before navigating, otherwise
              // ProtectedRoute can race us to /login.
              const ok = await waitForSession();
              if (!ok) {
                toast.error("Sign-in didn't complete. Please try again.");
                setLoading(false);
                return;
              }
              await ensureProfile();
              navigate("/");
            }}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to={inviteToken ? `/login?invite=${encodeURIComponent(inviteToken)}` : "/login"} className="font-medium text-primary hover:underline">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
